/* The native scroll position is the timeline. No wheel/touch interception. */
(function () {
  'use strict';
  const clamp = (n) => Math.max(0, Math.min(1, n));
  const pinTopAt = (height, viewport) => height > viewport ? viewport - height : (viewport - height) / 2;
  function progressAt(top, stageHeight, runwayHeight, viewportHeight) {
    return clamp((pinTopAt(stageHeight, viewportHeight) - top) / Math.max(1, runwayHeight - stageHeight));
  }
  function pinAt(top, stageHeight, runwayHeight, viewportHeight) {
    const pinTop = pinTopAt(stageHeight, viewportHeight);
    if (top >= pinTop) return 'before';
    return top + runwayHeight <= stageHeight + pinTop ? 'after' : 'fixed';
  }
  // onStuck : appelé quand un saut ne se termine pas alors que l'image visée est déjà chargée (plus de 2,5 s).
  // Sur iPhone, au retour sur la page, la vidéo peut rester sur « seeking » sans jamais émettre « seeked » :
  // plus aucun saut ne partait et le parcours restait figé sur une image.
  // Un saut vers une partie pas encore téléchargée n'est pas un blocage : on attend le réseau.
  function createSeekController(video, onStuck) {
    let target = 0, destroyed = false, since = null;
    const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());
    function loaded(time) {
      const ranges = video.buffered;
      if (!ranges) return false;
      for (let i = 0; i < ranges.length; i++) if (time >= ranges.start(i) - .1 && time <= ranges.end(i) + .1) return true;
      return false;
    }
    function seek() {
      if (destroyed || !Number.isFinite(video.duration) || !video.readyState) return;
      const time = Math.min(Math.max(0, video.duration - .04), target * video.duration);
      if (video.seeking) {
        if (since === null) since = now();
        else if (onStuck && now() - since > 2500 && loaded(time)) {since = null; onStuck();}
        return;
      }
      since = null;
      if (Math.abs(video.currentTime - time) > .035) {since = now(); video.currentTime = time;}
    }
    function done() {since = null; seek();}
    video.addEventListener('seeked', done);
    return {to(p) {target = clamp(p); seek();}, reset() {since = null;}, destroy() {destroyed = true; video.removeEventListener('seeked', done);}};
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = {progressAt, pinAt, createSeekController};
  if (typeof document === 'undefined') return;
  const instances = new Map();
  function init(root) {
    if (instances.has(root)) return;
    const runway = root.querySelector('.msu-runway');
    const stage = root.querySelector('.msu-stage');
    const video = root.querySelector('.msu-film');
    const room = root.querySelector('.msu-room');
    const garden = root.querySelector('.msu-garden');
    const skip = root.querySelector('.msu-skip');
    const reduce = matchMedia('(prefers-reduced-motion: reduce)');
    const mobile = matchMedia('(max-width: 749px)');
    if (!runway || !stage || !video || !room || !garden) return;
    let ready = false, raf = 0, observer, previous = '', destroyed = false;
    let stageHeight = 0, runwayHeight = 0, viewportHeight = 0;
    // Touch browsers fold their toolbar while scrolling, which resizes the window on every direction change.
    const touch = matchMedia('(hover: none) and (pointer: coarse)');
    let steadyWidth = -1, steadyViewport = 0;
    // Vidéo figée (retour par le cache de navigation, onglet resté en arrière-plan) : on recharge le média seul.
    // La mise en page ne bouge pas (pas de fallback) ; « loadeddata » relance activate(). Trois essais au plus par
    // affichage de la page, espacés de 4 s, pour ne pas boucler sur un réseau lent.
    let revivals = 0, lastRevival = 0;
    function revive() {
      if (destroyed || reduce.matches || !video.getAttribute('src')) return;
      const t = performance.now();
      if (revivals >= 3 || (lastRevival && t - lastRevival < 4000)) return;
      revivals += 1; lastRevival = t;
      controller.reset(); video.load();
    }
    const controller = createSeekController(video, revive);
    const events = new AbortController();
    const options = {signal: events.signal};
    const visible = (element, show) => {
      element.inert = !show;
      element.setAttribute('aria-hidden', String(!show));
    };
    function fallback() {
      ready = false;
      root.classList.remove('msu-journey-ready');
      root.removeAttribute('data-scene');
      root.removeAttribute('data-pin');
      visible(room, true); visible(garden, true);
      previous = '';
      runway.style.removeProperty('height');
    }
    function measure() {
      viewportHeight = window.innerHeight;
      stageHeight = room.getBoundingClientRect().height;
      // On touch devices the runway keeps the viewport height it had at this width. Following the
      // toolbar would move everything below the section under the finger (4.2 px per toolbar px).
      if (!touch.matches || window.innerWidth !== steadyWidth) {steadyWidth = window.innerWidth; steadyViewport = viewportHeight;}
      // Three viewport lengths provide a deliberate travelling without trapping scroll.
      // On phones the scene is 100dvh, so the stage term follows the toolbar too.
      runwayHeight = (touch.matches && mobile.matches ? steadyViewport : stageHeight) + steadyViewport * 3.2;
      if (ready) runway.style.height = runwayHeight + 'px';
      stage.style.setProperty('--msu-pin-top', pinTopAt(stageHeight, viewportHeight) + 'px');
      schedule();
    }
    function render() {
      raf = 0;
      if (!ready || destroyed) return;
      const top = runway.getBoundingClientRect().top;
      const pin = pinAt(top, stageHeight, runwayHeight, viewportHeight);
      if (root.dataset.pin !== pin) root.dataset.pin = pin;
      const p = progressAt(top, stageHeight, runwayHeight, viewportHeight);
      controller.to(p);
      // Decode before exposing the moving layer, especially on reverse scroll.
      const decoded = video.currentTime / video.duration;
      const scene = p <= .025 ? 'room' : p >= .975 ? 'garden' : 'travel';
      if (scene !== previous) {
        root.dataset.scene = scene;
        visible(room, scene === 'room'); visible(garden, scene === 'garden');
        previous = scene;
      }
      root.style.setProperty('--msu-progress', p);
      video.style.visibility = scene === 'travel' && decoded > 0 ? 'visible' : 'hidden';
    }
    function schedule() {if (!raf && !destroyed) raf = requestAnimationFrame(render);}
    function activate() {
      if (reduce.matches || !Number.isFinite(video.duration) || !video.videoWidth || destroyed) return;
      ready = true;
      root.classList.add('msu-journey-ready');
      measure();
    }
    function load() {
      if (reduce.matches || destroyed) return;
      const source = mobile.matches ? video.dataset.mobileSrc : video.dataset.desktopSrc;
      if (!source) {fallback(); return;}
      if (video.getAttribute('src') !== source) {
        fallback(); video.src = source; video.preload = 'auto'; video.load();
      } else if (video.readyState >= 2) activate();
    }
    function mediaChange() {fallback(); load(); measure();}
    video.addEventListener('loadeddata', activate, options);
    video.addEventListener('seeked', schedule, options);
    video.addEventListener('error', fallback, options);
    window.addEventListener('scroll', schedule, {...options, passive:true});
    window.addEventListener('resize', measure, {...options, passive:true});
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {revivals = 0; lastRevival = 0; revive();}
      schedule();
    }, options);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && ready && (video.seeking || video.readyState < 2)) revive();
    }, options);
    reduce.addEventListener('change', mediaChange, options);
    mobile.addEventListener('change', mediaChange, options);
    skip.addEventListener('click', (event) => {
      if (!ready) return;
      event.preventDefault();
      const top = runway.getBoundingClientRect().top + window.scrollY + runwayHeight - stageHeight - pinTopAt(stageHeight, viewportHeight);
      window.scrollTo({top, behavior:'instant'});
      render(); garden.querySelector('a')?.focus({preventScroll:true});
    }, options);
    const resize = new ResizeObserver(measure); resize.observe(room);
    observer = new IntersectionObserver((entries) => {if (entries.some(e => e.isIntersecting)) load();}, {rootMargin:'1200px 0px'});
    observer.observe(root);
    fallback();
    instances.set(root, () => {
      destroyed = true; events.abort(); observer.disconnect(); resize.disconnect();
      controller.destroy(); cancelAnimationFrame(raf); video.removeAttribute('src'); video.load();
      instances.delete(root);
    });
  }
  function scan(scope) {scope.querySelectorAll('[data-msu-journey]').forEach(init);}
  scan(document);
  document.addEventListener('shopify:section:load', e => scan(e.target));
  document.addEventListener('shopify:section:unload', e => {
    for (const [root, destroy] of instances) if (e.target.contains(root)) destroy();
  });
})();
